import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import styles from "../../css/Dashboard.module.css";
import { useAuth } from "../../contexts/AuthContext";

const ApplicationList = () => {
  const [applications, setApplications] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [sortOrder, setSortOrder] = useState("desc");
  const [successMessage, setSuccessMessage] = useState("");
  const navigate = useNavigate();
  const { userRole } = useAuth();

  useEffect(() => {
    // Redirect based on user role
    if (userRole === 'recruiter') {
      navigate('/recruiter/dashboard');
      return;
    } else if (userRole === 'universitypartner') {
      navigate('/university/dashboard');
      return;
    }

    fetchApplications();
  }, [sortOrder, navigate, userRole]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `https://techservicemarket-backend.onrender.com/api/users/profile/applications`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const sortedApplications = response.data.sort((a, b) => {
        const dateA = new Date(a.appliedAt);
        const dateB = new Date(b.appliedAt);
        return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
      });

      setApplications(sortedApplications);
    } catch (error) {
      console.error("Error fetching applications:", error);
      setError("An error occurred while fetching your applications.");
    } finally {
      setLoading(false);
    }
  };

  const handleSortByDate = () => {
    setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
  };

  const handleConfirmInterview = async (applicationId) => {
    try {
      setLoading(true);
      console.log("Confirming interview for application:", applicationId);
      
      const response = await axios.post(
        "https://techservicemarket-backend.onrender.com/api/users/profile/applications/updateApplication",
        { 
          applicationId, 
          status: "Interview Accepted" 
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            'Content-Type': 'application/json'
          },
        }
      );
      
      console.log("Interview confirmation response:", response.data);
      
      // Show success message
      setSuccessMessage("Interview confirmed! The recruiter has been notified.");
      
      // Hide the success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage("");
      }, 3000);
      
      // Refresh the applications list
      fetchApplications();
    } catch (error) {
      console.error("Error confirming interview:", error);
      setError(`Failed to confirm interview: ${error.response?.data?.message || error.message}`);
      
      // Hide the error message after 3 seconds
      setTimeout(() => {
        setError("");
      }, 3000);
    } finally {
      setLoading(false);
    }
  };

  // If not a regular user, don't render anything (will redirect)
  if (userRole !== 'user') {
    return <div className={styles.loading}>Redirecting...</div>;
  }

  if (loading) {
    return <div className={styles.loading}>Loading...</div>;
  }

  const getStatusClass = (status) => {
    switch (status) {
      case 'Accepted':
        return styles.acceptedApplication;
      case 'Rejected':
        return styles.rejectedApplication;
      case 'Interview Scheduled':
        return styles.interviewApplication;
      default:
        return '';
    }
  };

  return (
    <div className={styles.dashboardContainer}>
      <h1 className={styles.dashboardHeader}>Your Job Applications</h1>
      <div className={styles.contentContainer}>
        {/* Sorting Options */}
        <div className={styles.sortSection}>
          <button onClick={handleSortByDate} className={styles.sortButton}>
            Sort by Date Applied{" "}
            {sortOrder === "asc" ? "(old to new)" : "(new to old)"}
          </button>
        </div>

        {/* Application List Section */}
        <div className={styles.jobApplicationSection}>
          {(error || successMessage) && (
            <div className={error ? styles.errorBanner : styles.successBanner}>
              {error || successMessage}
            </div>
          )}
          {!error && applications.length > 0 ? (
            applications.map((application) => (
              <div key={application.applicationId} className={`${styles.jobCard} ${getStatusClass(application.status)}`}>
                <div className={styles.applicationHeader}>
                  <h3>{application.job.jobTitle || "Not available"}</h3>
                  <div className={styles.statusBadge}>
                    {application.status}
                    {application.status === 'Interview Scheduled' && (
                      <i className="fas fa-calendar-alt" title="Interview Scheduled"></i>
                    )}
                    {application.status === 'Accepted' && (
                      <i className="fas fa-check-circle" title="Application Accepted"></i>
                    )}
                    {application.status === 'Rejected' && (
                      <i className="fas fa-times-circle" title="Application Rejected"></i>
                    )}
                  </div>
                </div>
                <h4>
                  {application.job.jobCompany || "Not available"}
                </h4>
                <div className={styles.sortSection}>
                  <button 
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = `data:${application.resume.contentType};base64,${application.resume.data}`;
                      link.download = application.resume.filename;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }}
                    className={styles.sortButton}>
                    Download Resume
                  </button>
                </div>
                <h4>Cover Letter{" "}</h4>
                  <div>
                    <textarea
                      rows="5"
                      cols="75"
                      className={styles.interviewInfoInput}
                      value={application.coverLetter || "Not provided"}
                    />
                  </div>
                
                <h4>
                  Applied On{" "}
                </h4>
                <p className={styles.jobCardText}>
                  {new Date(application.appliedAt).toLocaleString()}
                </p>
                <h4>
                  Status: {application.status}
                </h4>
            
                {/* Only show interview information and date if status is not "Rejected" */}
                {application.status !== "Rejected" && (
                  <>                    
                      <h4
                        htmlFor={`interviewInfo-${application.applicationId}`}>
                        Interview Information
                      </h4>                      
                      <textarea
                        rows="5"
                        cols="75"
                        id={`interviewInfo-${application.applicationId}`}
                        className={styles.interviewInfoInput}
                        value={application.interviewInformation || "Details not provided"}
                        placeholder="Provide interview details here..."
                        readOnly // Makes the textarea inactive
                      />                    
                    <div className={styles.jobActions}>
                      <div>
                        <h4 htmlFor={`interviewDate-${application.applicationId}`}>
                          Interview Date </h4>                        
                        <input
                          type="datetime-local"
                          id={`interviewDate-${application.applicationId}`}
                          value={
                            application.interviewDate
                              ? new Date(application.interviewDate).toISOString().slice(0, -1)
                              : ""
                          }
                          disabled // Makes the field inactive
                          className={styles.interviewDateInput}
                        />
                      </div>
                      {application.status === "Interview Scheduled" && (
                        <button
                          onClick={() => handleConfirmInterview(application.applicationId)}
                          className={styles.inviteButton}
                        >
                          Confirm Interview
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))
          ) : (
            <p className={styles.noApplicationsText}>No applications found.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApplicationList;
